import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import InvButton from './InvButton';
import { InvTextField, InvSelect } from './InvField';
import { PAYMENT_MODES } from '../constants';
import { formatINR, parseMoney } from '../utils';
import { IPD_COLORS } from '../theme';

const MODE_OPTIONS = PAYMENT_MODES.map((m) => ({ value: m, label: m }));

function emptyRow() {
  return { mode: 'CASH', amount: '', txn_ref: '' };
}

/**
 * Split tender collector / payout UI.
 * Props:
 *   totalDue     number|string — exact amount that modes must sum to
 *   direction    "collect" | "payout"
 *   onSubmit(modes) — modes: [{ mode, amount, txn_ref? }]
 *   submitting   bool
 *   disabled     bool
 *   autoFill     bool — seed / sync the first row from totalDue when untouched
 *   compact      bool — stacked right-panel layout
 *   hideHeading  bool — hide the inner "Collect payment" / "Refund via" title
 *   remainingLabel, modeLabel, amountLabel — pharmacist-facing copy overrides
 */
export default function SplitPaymentCollector({
  totalDue,
  direction = 'collect',
  onSubmit,
  submitting = false,
  disabled = false,
  autoFill = false,
  compact = false,
  hideHeading = false,
  remainingLabel = 'Remaining to allocate',
  modeLabel = 'Mode *',
  amountLabel = 'Amount *',
}) {
  const due = parseMoney(totalDue);
  const [rows, setRows] = useState([emptyRow()]);
  const lastAutoFilled = useRef(null);

  useEffect(() => {
    if (!autoFill) return;
    const dueStr = due > 0 ? due.toFixed(2) : '';
    setRows((prev) => {
      if (prev.length !== 1) return prev;
      const current = parseMoney(prev[0].amount);
      const untouched = prev[0].amount === ''
        || lastAutoFilled.current == null
        || Math.abs(current - lastAutoFilled.current) < 0.005;
      if (!untouched) return prev;
      lastAutoFilled.current = due;
      if (prev[0].amount === dueStr) return prev;
      return [{ ...prev[0], amount: dueStr }];
    });
  }, [due, autoFill]);

  const allocated = useMemo(
    () => rows.reduce((sum, r) => sum + parseMoney(r.amount), 0),
    [rows],
  );
  const remaining = Math.round((due - allocated) * 100) / 100;
  const remainingSettled = Math.abs(remaining) < 0.005;

  const invalidTxnRef = rows.some((r) => r.mode !== 'CASH' && !String(r.txn_ref || '').trim());
  const canSubmit = !disabled
    && !submitting
    && due > 0
    && remainingSettled
    && rows.every((r) => parseMoney(r.amount) > 0)
    && !invalidTxnRef;

  function updateRow(index, patch) {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function handleSubmit() {
    if (!canSubmit) return;
    const modes = rows.map((r) => ({
      mode: r.mode,
      amount: String(parseMoney(r.amount).toFixed(4)),
      ...(r.mode !== 'CASH' && r.txn_ref ? { txn_ref: r.txn_ref.trim() } : {}),
    }));
    onSubmit?.(modes);
  }

  function renderRemoveButton(i) {
    if (compact && rows.length <= 1) return null;
    return (
      <IconButton
        size="small"
        disabled={rows.length <= 1 || disabled || submitting}
        onClick={() => setRows((prev) => prev.filter((_, idx) => idx !== i))}
        aria-label="Remove payment mode"
        sx={{
          color: IPD_COLORS.error.text,
          width: 40,
          height: 40,
          flexShrink: 0,
        }}
      >
        <DeleteIcon fontSize="small" />
      </IconButton>
    );
  }

  return (
    <Box>
      {!hideHeading && (
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: compact ? 1 : 1.5, color: IPD_COLORS.textPrimary }}>
          {direction === 'payout' ? 'Refund via' : 'Collect payment'}
        </Typography>
      )}

      {rows.map((row, i) => (
        <Box
          key={i}
          sx={{
            display: 'flex',
            flexDirection: compact ? 'column' : 'row',
            gap: 1,
            mb: compact ? 0.75 : 1.25,
            alignItems: compact ? 'stretch' : 'center',
            flexWrap: compact ? 'nowrap' : 'wrap',
          }}
        >
          <Box sx={{ width: compact ? '100%' : 140 }}>
            <InvSelect
              label={modeLabel}
              value={row.mode}
              disabled={disabled || submitting}
              options={MODE_OPTIONS}
              onChange={(e) => updateRow(i, { mode: e.target.value })}
            />
          </Box>
          <Box sx={{
            width: compact ? '100%' : 140,
            display: 'flex',
            gap: 1,
            alignItems: compact ? 'flex-start' : 'center',
          }}>
            <Box sx={{ flex: 1, minWidth: 0, width: compact ? 'auto' : 140 }}>
              <InvTextField
                label={amountLabel}
                type="number"
                value={row.amount}
                disabled={disabled || submitting}
                inputProps={{ min: 0, step: '0.01', style: { textAlign: 'right' } }}
                onChange={(e) => updateRow(i, { amount: e.target.value })}
              />
            </Box>
            {compact && renderRemoveButton(i)}
          </Box>
          {row.mode !== 'CASH' && (
            <Box sx={{ flex: 1, minWidth: compact ? 0 : 160, width: compact ? '100%' : 'auto' }}>
              <InvTextField
                label="Txn ref *"
                value={row.txn_ref}
                disabled={disabled || submitting}
                onChange={(e) => updateRow(i, { txn_ref: e.target.value })}
              />
            </Box>
          )}
          {!compact && renderRemoveButton(i)}
        </Box>
      ))}

      <Box sx={{
        display: 'flex',
        justifyContent: compact ? 'flex-start' : 'space-between',
        alignItems: compact ? 'stretch' : 'center',
        flexDirection: compact ? 'column' : 'row',
        mt: compact ? 0.25 : 1,
        mb: compact ? 1 : 2,
        gap: compact ? 1 : 0,
      }}>
        {compact && (
          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: IPD_COLORS.textSecondary, mb: 0.25 }}>
              {remainingLabel}
            </Typography>
            <Typography sx={{
              fontSize: 16,
              fontWeight: 800,
              fontVariantNumeric: 'tabular-nums',
              color: remainingSettled ? IPD_COLORS.success.text : IPD_COLORS.warning.text,
            }}>
              {formatINR(remaining)}
            </Typography>
          </Box>
        )}
        <InvButton
          size="small"
          variant="outlined"
          tone="teal"
          startIcon={<AddIcon />}
          disabled={disabled || submitting}
          onClick={() => setRows((prev) => [...prev, emptyRow()])}
        >
          Add mode
        </InvButton>
        {!compact && (
          <Typography sx={{
            fontSize: 13,
            fontWeight: 700,
            color: remainingSettled ? IPD_COLORS.success.text : IPD_COLORS.warning.text,
          }}>
            {remainingLabel}: {formatINR(remaining)}
          </Typography>
        )}
      </Box>

      <InvButton
        fullWidth
        loading={submitting}
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        {direction === 'payout' ? 'Settle refund' : 'Collect & dispense'}
      </InvButton>
    </Box>
  );
}
